<template>
  <div :class="wrapperClass">
    <header class="border-grays-light flex flex-col border-b px-[18px] py-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2 font-bold">
          <div>
            <nuxt-link to="/products/list">
              <img
                src="~/assets/images/zenni-logo.png"
                alt="zenni logo"
                class="w-25 h-[32px]"
              />
            </nuxt-link>
          </div>
          <div
            class="text-teal-primary ml-3 text-lg xl:hidden"
            v-if="!isAmazonComputed"
          >
            Vision Center
          </div>
        </div>
        <div class="flex items-center space-x-4">
          <div
            v-if="showHeaderCartIcon"
            class="relative cursor-pointer"
            @click="goToCartReviewPage"
          >
            <ZenniIconsIconCart
              :class="`${
                frameItemQty ? 'text-teal-primary' : 'text-grays-dark'
              }`"
            />
            <sup
              v-if="frameItemQty"
              class="bg-teal-primary absolute right-0 top-[-6px] inline-block h-[16px] w-[16px] rounded-full text-center leading-normal text-white"
              :class="`${
                frameItemQty ? 'text-teal-primary' : 'text-grays-dark'
              }`"
              >{{ frameItemQty }}</sup
            >
          </div>
          <HeaderUserDropdown
            :show-header-user-icon="showHeaderUserIcon"
            @logout="handleLogout"
          >
            <template #userContent>
              <ZenniIconsIconUser
                :class="`${
                  isOrderOrTrackingUrl
                    ? 'text-teal-primary'
                    : 'text-grays-dark cursor-pointer'
                }`"
              />
            </template>
          </HeaderUserDropdown>
        </div>
      </div>
      <AppHeaderCreditBar
        v-if="
          showHeaderCreditIcon &&
          userStore.userGift?.usage?.type === 'giftCard' &&
          userHasCredit
        "
        class="ml-auto mt-3"
      />
    </header>
    <div v-if="!hideBackButton" class="px-4 py-2">
      <img
        class="cursor-pointer"
        src="~/assets/images/icon-chevrons.svg"
        alt="back button"
        @click="executeBackButtonFunction"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { twMerge, type ClassNameValue } from 'tailwind-merge'
import { computed, useAttrs, onMounted } from 'vue'

import { useCartStore } from '@/store/cart'
import cartGetters from '~/utils/getters/cartGetters'
import { useUserStore } from '~/store/user'
import useCart from '~/composables/useCart'
import useUiState from '~/composables/useUiState'
import { useRouter, useRoute } from '#imports'
import { useEyebotStore } from '~/store/eyebotState'
import { useEcpStore } from '~/store/ecpState'
import { useAmazonState } from '~/composables/useAmazonState'
const router = useRouter()
const route = useRoute()

const eyebotStore = useEyebotStore()
const ecpStore = useEcpStore()
const cartStore = useCartStore()
const { cart, load: loadCart } = useCart()
const userStore = useUserStore()
const {
  showHeaderCreditIcon,
  showHeaderCartIcon,
  showHeaderUserIcon,
  hideBackButton,
  executeBackButtonFunction,
} = useUiState()

const { isAmazon } = useAmazonState()
const isAmazonComputed = computed(() => isAmazon())

defineOptions({
  name: 'AppHeader',
  inheritAttrs: false,
})

const attrs = useAttrs()

const wrapperClass = computed(() =>
  twMerge(
    (attrs?.class as ClassNameValue) ?? '',
    'sticky top-0 z-50 w-full bg-white h-fit',
  ),
)

// Ensure cart is loaded when component mounts
onMounted(async () => {
  // Check if we're on a login page before loading cart
  const isLoginRoute =
    route.path.includes('/login') || route.path.includes('/amazon/login')

  if (userStore.loggedIn && !isLoginRoute) {
    await loadCart()
  }
})

const frameItemQty = computed(() => cartGetters.getTotalQty(cart.value) || null)

const isOrderOrTrackingUrl = computed(
  () => route.path === '/user/order' || route.path === '/tracking',
)

const userHasCredit = computed<boolean>(() => {
  const balance = userStore.userGift?.usage?.balance ?? 0
  if (balance) {
    return balance > 0
  }
  return false
})

const goToCartReviewPage = () => {
  if (route.fullPath !== '/checkout/cartpreview') {
    cartStore.setCartBackPath(route.fullPath)
  }
  if (!frameItemQty.value) {
    return router.push('/checkout/empty')
  }
  router.push('/checkout/cartpreview')
}

const handleLogout = (): void => {
  eyebotStore.clearEyeBotData()
  userStore.logout()
  ecpStore.clearEcpState()
  router.go(0)
}
</script>
